import React, { useCallback, useMemo, useRef } from 'react'
import Badge from 'components/Luxkit/Badge'
import LineShoppingCartIcon from 'components/Luxkit/Icons/line/LineShoppingCartIcon'
import NavBarButton from '../NavBarButton/NavBarButton'
import { useAppSelector } from 'hooks/reduxHooks'
import { getNumberOfActiveUserCartItems } from 'selectors/userCart'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import useToggle from 'hooks/useToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { matchPath } from 'react-router'
import UserCartDropdownPreview from 'components/Pages/UserCartPage/UserCartPreview/UserCartDropdownPreview'
import debounce from 'lodash/debounce'

interface Props {
  className?: string;
}

/**
 * This is for a not-yet implemented cart feature that is being worked
 * on by the CRO team
 */
function NavBarCartButton(props: Props) {
  const enabled = useAppSelector((state: App.State) => isMultiCartEnabled(state))
  const numberOfActiveItems = useAppSelector(getNumberOfActiveUserCartItems)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const isCartPage = useMemo(() => !!matchPath(currentPath, { path: '/:regionCode/cart', exact: true }), [currentPath])

  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isHovered, , openHovered, closeHovered] = useToggle()

  const canShowCartPreview = numberOfActiveItems > 0 && !isCartPage
  const showCartPreview = isHovered && canShowCartPreview

  const debouncedCloseHovered = useMemo(() => debounce(closeHovered, 500), [closeHovered])

  const handleMouseEnter = useCallback(() => {
    if (canShowCartPreview) {
      debouncedCloseHovered.cancel()
      openHovered()
    }
  }, [canShowCartPreview, debouncedCloseHovered, openHovered])

  if (!enabled) {
    return null
  }

  return (<div onMouseEnter={handleMouseEnter} onMouseLeave={debouncedCloseHovered}>
    <NavBarButton
      ref={buttonRef}
      className={props.className}
      data-testid="cart-button"
      to="/cart"
      onClick={closeHovered}
      icon={<LineShoppingCartIcon />}
      badge={!!numberOfActiveItems && <Badge mode="critical" kind="string">
        {numberOfActiveItems}
      </Badge>}
    />
    <CSSBreakpoint min="desktop">
      <UserCartDropdownPreview open={showCartPreview} onClose={closeHovered} buttonRef={buttonRef} />
    </CSSBreakpoint>
  </div>)
}

export default React.memo(NavBarCartButton)
